import { useState, useEffect, useMemo } from "react";
import { useApprovalDetail } from "@/hooks/useApprovalSystem";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { REQUEST_TYPE_CONFIG, STATUS_STYLES, BILL_STATUS_CONFIG } from "./ApprovalRequestTypeConfig";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Building2, Calendar, DollarSign, FileText, Hash, Printer, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportPdf } from "@/lib/export";
import { ApprovalPrintTemplate } from "@/components/shared/print-templates/ApprovalPrintTemplate";

interface Props {
  requestId: string;
  showActions?: boolean;
  selectedLineIds?: Set<string>;
  onSelectedLineIdsChange?: (ids: Set<string>) => void;
  onTotalLineCount?: (count: number) => void;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

/* Parse "Water proof — Unit 547" → { base: "Water proof", unit: "547" } */
function parseLineDesc(desc: string): { base: string; unit: string | null } {
  const match = desc.match(/^(.+?)\s*[—·\-–]\s*Units?\s*(.+)$/i);
  if (match) return { base: match[1].trim(), unit: match[2].trim() };
  return { base: desc, unit: null };
}

interface BillLine {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface LineGroup {
  base: string;
  lines: BillLine[];
  totalQty: number;
  totalAmount: number;
}

function groupLines(lines: BillLine[]): LineGroup[] {
  const map = new Map<string, LineGroup>();
  for (const line of lines) {
    const { base } = parseLineDesc(line.description);
    const existing = map.get(base);
    if (existing) {
      existing.lines.push(line);
      existing.totalQty += line.quantity;
      existing.totalAmount += line.line_total;
    } else {
      map.set(base, {
        base,
        lines: [line],
        totalQty: line.quantity,
        totalAmount: line.line_total,
      });
    }
  }
  return Array.from(map.values());
}

export const ApprovalDetailPanel = ({
  requestId,
  selectedLineIds,
  onSelectedLineIdsChange,
  onTotalLineCount,
}: Props) => {
  const { data, isLoading } = useApprovalDetail(requestId);
  const { data: companyProfile } = useCompanyProfile();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const linkedBillId = data?.request?.linked_bill_id ?? null;
  const { data: billLines = [] } = useQuery({
    queryKey: ["bill_lines_for_approval", linkedBillId],
    queryFn: async () => {
      const { data: lines, error } = await supabase
        .from("bill_lines")
        .select("id, description, quantity, unit_price, line_total")
        .eq("bill_id", linkedBillId!)
        .order("created_at");
      if (error) throw error;
      return lines ?? [];
    },
    enabled: !!linkedBillId,
  });

  const groups = useMemo(() => groupLines(billLines as BillLine[]), [billLines]);
  const allLineIds = useMemo(() => new Set(billLines.map((l: any) => l.id)), [billLines]);

  // Report total line count to parent
  useEffect(() => {
    onTotalLineCount?.(billLines.length);
  }, [billLines.length, onTotalLineCount]);

  // Initialize selection with all lines when bill lines load
  useEffect(() => {
    if (billLines.length > 0 && selectedLineIds && selectedLineIds.size === 0 && onSelectedLineIdsChange) {
      onSelectedLineIdsChange(new Set(billLines.map((l: any) => l.id)));
    }
  }, [billLines]);

  const selected = selectedLineIds ?? new Set<string>();
  const allSelected = allLineIds.size > 0 && allLineIds.size === selected.size;
  const someSelected = selected.size > 0 && selected.size < allLineIds.size;

  const toggleAll = () => {
    if (!onSelectedLineIdsChange) return;
    onSelectedLineIdsChange(allSelected ? new Set() : new Set(allLineIds));
  };

  const toggleGroup = (group: LineGroup) => {
    if (!onSelectedLineIdsChange) return;
    const groupIds = group.lines.map((l) => l.id);
    const allGroupSelected = groupIds.every((id) => selected.has(id));
    const next = new Set(selected);
    for (const id of groupIds) {
      if (allGroupSelected) next.delete(id);
      else next.add(id);
    }
    onSelectedLineIdsChange(next);
  };

  const toggleLine = (lineId: string) => {
    if (!onSelectedLineIdsChange) return;
    const next = new Set(selected);
    if (next.has(lineId)) next.delete(lineId);
    else next.add(lineId);
    onSelectedLineIdsChange(next);
  };

  const toggleExpand = (base: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(base)) next.delete(base);
      else next.add(base);
      return next;
    });
  };

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-muted-foreground">Request not found.</div>;

  const { request, assignees } = data;
  const typeConfig = REQUEST_TYPE_CONFIG[request.request_type] ?? REQUEST_TYPE_CONFIG.other;
  const requestData = request.request_data ?? {};
  const bill = request.linked_bill;
  const draw = request.linked_draw;

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* ── Header ── */}
      <div className="p-4 border-b space-y-2">
        <div className="flex items-center gap-2">
          <span className={cn("h-3 w-3 rounded-full", typeConfig.dotClass)} />
          <span className={cn("text-xs font-semibold uppercase tracking-wider", typeConfig.color)}>
            {typeConfig.label}
          </span>
          <Badge className={cn("ml-auto text-xs", STATUS_STYLES[request.status])}>
            {request.status.replace("_", " ")}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Print / Save as PDF"
            onClick={() => {
              const events = (data as any).events ?? [];
              exportPdf(
                <ApprovalPrintTemplate
                  data={{
                    title: request.title,
                    request_type: request.request_type,
                    status: request.status,
                    description: request.description,
                    urgency: request.urgency,
                    project_name: request.projects?.name,
                    company_name: companyProfile?.name,
                    company_logo_url: companyProfile?.logo_url,
                    created_at: request.requested_at,
                    resolved_at: request.resolved_at,
                    resolution_note: request.resolution_note,
                    events: events.map((ev: any) => ({
                      event_type: ev.event_type,
                      comment: ev.comment,
                      created_at: ev.created_at,
                      actor_email: ev.actor_email,
                    })),
                  }}
                />,
                `Approval - ${request.title}`
              );
            }}
          >
            <Printer className="h-4 w-4" />
          </Button>
        </div>
        <h2 className="text-lg font-bold">{request.title}</h2>
        {request.description && (
          <p className="text-sm text-muted-foreground">{request.description}</p>
        )}
      </div>

      {/* ── Bill Details ── */}
      {bill && (
        <div className="p-4 border-b space-y-3">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Bill Details</h3>

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-semibold text-sm truncate">
                {bill.vendors?.name ?? "Unknown Vendor"}
              </span>
            </div>
            {(() => {
              const cfg = BILL_STATUS_CONFIG[bill.status];
              return (
                <Badge variant="outline" className={cn("text-xs shrink-0", cfg?.color ?? "bg-muted text-muted-foreground")}>
                  {cfg?.label ?? bill.status}
                </Badge>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Hash className="h-3.5 w-3.5" />
              <span>Bill #</span>
            </div>
            <span className="font-medium">{bill.bill_number}</span>

            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>Bill Date</span>
            </div>
            <span className="font-medium">
              {format(new Date(bill.bill_date), "MMM d, yyyy")}
            </span>

            {bill.due_date && (
              <>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Due Date</span>
                </div>
                <span className="font-medium">
                  {format(new Date(bill.due_date), "MMM d, yyyy")}
                </span>
              </>
            )}

            {bill.description && (
              <>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" />
                  <span>Description</span>
                </div>
                <span className="font-medium">{bill.description}</span>
              </>
            )}
          </div>

          {/* ── Consolidated Line Items ── */}
          {groups.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold text-muted-foreground">Line Items</h4>
              <div className="rounded-lg border overflow-hidden">
                {/* Header with select all */}
                <div className="flex items-center justify-between bg-muted/50 px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      className={someSelected ? "data-[state=unchecked]:bg-primary/20" : ""}
                    />
                    <span className="text-xs font-medium">
                      {allSelected ? "Deselect All" : "Select All"}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground tabular-nums">
                    Subtotal: {fmt(billLines.reduce((s: number, l: any) => s + l.line_total, 0))}
                  </span>
                </div>

                {/* Grouped rows */}
                {groups.map((group) => {
                  const groupIds = group.lines.map((l) => l.id);
                  const allGroupChecked = groupIds.every((id) => selected.has(id));
                  const someGroupChecked = groupIds.some((id) => selected.has(id)) && !allGroupChecked;
                  const isExpanded = expandedGroups.has(group.base);
                  const hasMultiple = group.lines.length > 1;

                  return (
                    <div key={group.base}>
                      {/* Group header row */}
                      <div
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 border-b hover:bg-muted/30 transition-colors",
                          !allGroupChecked && !someGroupChecked && "opacity-60"
                        )}
                      >
                        <Checkbox
                          checked={allGroupChecked}
                          onCheckedChange={() => toggleGroup(group)}
                          className={someGroupChecked ? "data-[state=unchecked]:bg-primary/20" : ""}
                        />
                        {hasMultiple ? (
                          <button
                            onClick={() => toggleExpand(group.base)}
                            className="flex items-center gap-1 text-xs font-medium hover:underline"
                          >
                            <ChevronRight
                              className={cn(
                                "h-3.5 w-3.5 transition-transform",
                                isExpanded && "rotate-90"
                              )}
                            />
                            {group.base}
                            <span className="text-muted-foreground">
                              ({group.lines.length} unit{group.lines.length !== 1 ? "s" : ""})
                            </span>
                          </button>
                        ) : (
                          <span className="text-xs font-medium">{group.lines[0].description}</span>
                        )}
                        <div className="ml-auto flex items-center gap-4 text-xs tabular-nums">
                          <span className="text-muted-foreground">
                            {hasMultiple
                              ? group.lines.map((l) => l.quantity).join(" + ")
                              : group.totalQty}
                          </span>
                          <span className="font-medium w-20 text-right">{fmt(group.totalAmount)}</span>
                        </div>
                      </div>

                      {/* Expanded unit lines */}
                      {isExpanded && hasMultiple && (
                        <div className="bg-muted/10">
                          {group.lines.map((line) => {
                            const { unit } = parseLineDesc(line.description);
                            return (
                              <div
                                key={line.id}
                                className={cn(
                                  "flex items-center gap-2 pl-10 pr-3 py-1.5 border-b last:border-b-0",
                                  !selected.has(line.id) && "opacity-50"
                                )}
                              >
                                <Checkbox
                                  checked={selected.has(line.id)}
                                  onCheckedChange={() => toggleLine(line.id)}
                                />
                                <span className="text-xs text-muted-foreground">
                                  {unit ? `Unit ${unit}` : line.description}
                                </span>
                                <div className="ml-auto flex items-center gap-4 text-xs tabular-nums">
                                  <span className="text-muted-foreground">qty {line.quantity}</span>
                                  <span className="text-muted-foreground">{fmt(line.unit_price)}</span>
                                  <span className="font-medium w-20 text-right">{fmt(line.line_total)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Amount summary */}
          <div className="rounded-lg bg-muted/50 border p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{fmt(bill.subtotal)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold">
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                <span>Total</span>
              </div>
              <span className="text-base">{fmt(bill.total)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Draw Details ── */}
      {draw && (
        <div className="p-4 border-b space-y-3">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Draw Details</h3>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-semibold text-sm">Draw #{draw.draw_number}</span>
            </div>
            {(() => {
              const cfg = BILL_STATUS_CONFIG[draw.status];
              return (
                <Badge variant="outline" className={cn("text-xs shrink-0", cfg?.color ?? "bg-muted text-muted-foreground")}>
                  {cfg?.label ?? draw.status}
                </Badge>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>Generated</span>
            </div>
            <span className="font-medium">
              {format(new Date(draw.created_at), "MMM d, yyyy")}
            </span>
            {draw.approved_at && (
              <>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Approved</span>
                </div>
                <span className="font-medium">
                  {format(new Date(draw.approved_at), "MMM d, yyyy")}
                </span>
              </>
            )}
          </div>
          <div className="rounded-lg bg-muted/50 border p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{fmt(draw.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Retainage</span>
              <span className="text-amber-700">−{fmt(draw.retainage_amount)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold">
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                <span>Net Due</span>
              </div>
              <span className="text-base">{fmt(draw.net_due)}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Request Details ── */}
      <div className="p-4 border-b space-y-2">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Request Details</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <span className="text-muted-foreground">Project</span>
          <span className="font-medium">{request.projects?.name ?? "—"}</span>

          <span className="text-muted-foreground">Requested</span>
          <span className="font-medium">
            {format(new Date(request.requested_at), "MMM d, yyyy h:mm a")}
          </span>

          <span className="text-muted-foreground">Mode</span>
          <span className="font-medium capitalize">
            {request.approval_mode === "any" ? "Any one approves" : "All must approve"}
          </span>

          {Object.entries(requestData)
            .filter(([key]) => !["vendor_name", "bill_number", "bill_date", "due_date", "total", "subtotal"].includes(key))
            .map(([key, value]) => (
              <div key={key} className="contents">
                <span className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}</span>
                <span className="font-medium">
                  {typeof value === "number" ? fmt(value) : String(value ?? "—")}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
